<template>
  <div class="meeting-details-page">
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64" />
      <p class="text-h6 mt-4">Loading meeting...</p>
    </div>

    <div v-else-if="meeting">
      <!-- Page Header -->
      <div class="d-flex justify-space-between align-start mb-6">
        <div class="flex-grow-1">
          <div class="d-flex align-center mb-2">
            <v-btn
              icon
              variant="text"
              @click="$router.go(-1)"
              class="mr-2"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-chip
              :color="getStatusColor(meeting.status)"
              variant="flat"
              class="mr-3"
            >
              {{ meeting.status.toUpperCase() }}
            </v-chip>
            <v-chip
              :color="meeting.meeting_type?.color || 'primary'"
              variant="flat"
              class="mr-3"
            >
              {{ meeting.meeting_type?.name || 'Meeting' }}
            </v-chip>
          </div>
          
          <h1 class="text-h4 font-weight-bold text-primary mb-2">
            {{ meeting.title }}
          </h1>
          
          <div class="d-flex align-center text-medium-emphasis mb-4">
            <v-icon size="small" class="mr-2">mdi-calendar</v-icon>
            <span class="mr-4">{{ formatDateTime(meeting.start_datetime) }}</span>
            <v-icon size="small" class="mr-2">mdi-map-marker</v-icon>
            <span class="mr-4">{{ meeting.location || 'Location TBD' }}</span>
            <v-icon size="small" class="mr-2">mdi-account-group</v-icon>
            <span>{{ meeting.attendance_count }} attendees</span>
          </div>
        </div>

        <div class="d-flex align-center gap-2">
          <v-btn
            v-if="canEdit"
            color="primary"
            variant="outlined"
            @click="editMeeting"
            prepend-icon="mdi-pencil"
          >
            Edit
          </v-btn>
          
          <v-btn
            v-if="meeting.virtual_link"
            color="success"
            variant="outlined"
            @click="joinMeeting"
            prepend-icon="mdi-video"
          >
            Join Meeting
          </v-btn>

          <v-btn
            color="secondary"
            variant="outlined"
            @click="shareMeeting"
            prepend-icon="mdi-share"
          >
            Share
          </v-btn>

          <v-menu>
            <template #activator="{ props }">
              <v-btn
                icon
                variant="text"
                v-bind="props"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item @click="printMeeting">
                <v-list-item-title>Print</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-printer</v-icon>
                </template>
              </v-list-item>
              <v-list-item @click="exportMeeting">
                <v-list-item-title>Export PDF</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-file-pdf-box</v-icon>
                </template>
              </v-list-item>
              <v-divider />
              <v-list-item v-if="canDelete" @click="deleteMeeting" class="text-error">
                <v-list-item-title>Delete</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-delete</v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <v-row>
        <!-- Main Content -->
        <v-col cols="12" md="8">
          <!-- Meeting Description -->
          <v-card class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Meeting Description
            </v-card-title>
            <v-card-text>
              <p class="text-body-1" style="white-space: pre-wrap;">{{ meeting.description }}</p>
            </v-card-text>
          </v-card>

          <!-- Agenda -->
          <v-card v-if="meeting.agenda" class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              <v-icon class="mr-2">mdi-format-list-numbered</v-icon>
              Agenda
            </v-card-title>
            <v-card-text>
              <div class="agenda-content">
                <pre class="text-body-1" style="white-space: pre-wrap; font-family: inherit;">{{ meeting.agenda }}</pre>
              </div>
            </v-card-text>
          </v-card>

          <!-- Attendance -->
          <v-card class="mb-6">
            <v-card-title class="d-flex justify-space-between align-center">
              <span class="text-h6 font-weight-medium">
                <v-icon class="mr-2">mdi-account-group</v-icon>
                Attendance ({{ meeting.attendance_count }})
              </span>
              <v-btn
                v-if="canManageAttendance"
                size="small"
                variant="outlined"
                @click="manageAttendance"
              >
                Manage Attendance
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div v-if="meeting.attendance && meeting.attendance.length > 0">
                <v-row>
                  <v-col 
                    v-for="attendance in meeting.attendance" 
                    :key="attendance.id"
                    cols="12" sm="6" md="4"
                  >
                    <v-card variant="outlined" class="attendance-card">
                      <v-card-text class="d-flex align-center pa-3">
                        <v-avatar
                          :color="getAttendanceStatusColor(attendance.status)"
                          size="40"
                          class="mr-3"
                        >
                          <v-icon color="white">
                            {{ getAttendanceIcon(attendance.status) }}
                          </v-icon>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div class="font-weight-medium">{{ attendance.user?.full_name }}</div>
                          <div class="text-caption text-medium-emphasis">
                            {{ attendance.status.replace('_', ' ').toUpperCase() }}
                          </div>
                          <div v-if="attendance.checked_in_at" class="text-caption">
                            Checked in: {{ formatTime(attendance.checked_in_at) }}
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
              <div v-else class="text-center py-6 text-medium-emphasis">
                <v-icon size="48" class="mb-3">mdi-account-group</v-icon>
                <p>No attendance recorded yet</p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Meeting Documents -->
          <v-card v-if="documents && documents.length > 0" class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              <v-icon class="mr-2">mdi-file-document-multiple</v-icon>
              Meeting Documents ({{ documents.length }})
            </v-card-title>
            <v-card-text>
              <div class="document-grid">
                <div
                  v-for="document in documents"
                  :key="document.id"
                  class="document-item d-flex align-center pa-3 mb-2"
                  @click="viewDocument(document)"
                >
                  <v-icon
                    :color="getDocumentColor(document.document_type)"
                    size="32"
                    class="mr-3"
                  >
                    {{ getDocumentIcon(document.document_type) }}
                  </v-icon>
                  <div class="flex-grow-1">
                    <div class="font-weight-medium">{{ document.title }}</div>
                    <div class="text-caption text-medium-emphasis">{{ document.document_type }}</div>
                    <div v-if="document.description" class="text-body-2 mt-1">{{ document.description }}</div>
                  </div>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    @click.stop="downloadDocument(document)"
                  >
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Meeting Resolutions -->
          <v-card v-if="resolutions && resolutions.length > 0" class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              <v-icon class="mr-2">mdi-gavel</v-icon>
              Meeting Resolutions ({{ resolutions.length }})
            </v-card-title>
            <v-card-text>
              <div class="resolution-list">
                <v-card
                  v-for="resolution in resolutions"
                  :key="resolution.id"
                  variant="outlined"
                  class="mb-3 resolution-card"
                  @click="viewResolution(resolution)"
                >
                  <v-card-text class="pa-4">
                    <div class="d-flex justify-space-between align-start mb-2">
                      <div class="flex-grow-1">
                        <h4 class="text-h6 font-weight-medium mb-1">{{ resolution.title }}</h4>
                        <p class="text-body-2 text-medium-emphasis mb-2">{{ resolution.resolution_number }}</p>
                      </div>
                      <div class="d-flex align-center gap-2">
                        <v-chip
                          :color="getResolutionStatusColor(resolution.status)"
                          size="small"
                          variant="flat"
                        >
                          {{ resolution.status.toUpperCase() }}
                        </v-chip>
                        <v-btn
                          v-if="resolution.status === 'voting' && !userHasVoted(resolution)"
                          color="primary"
                          size="small"
                          variant="outlined"
                          @click.stop="goToVoting(resolution)"
                        >
                          Vote Now
                        </v-btn>
                        <v-chip
                          v-else-if="userHasVoted(resolution)"
                          color="success"
                          size="small"
                          variant="flat"
                        >
                          Voted
                        </v-chip>
                      </div>
                    </div>
                    <p class="text-body-2 mb-3">{{ resolution.description }}</p>
                    <div v-if="resolution.vote_summary" class="vote-summary">
                      <div class="d-flex gap-3 text-caption">
                        <span class="text-success">For: {{ resolution.vote_summary.approve }}</span>
                        <span class="text-error">Against: {{ resolution.vote_summary.reject }}</span>
                        <span class="text-warning">Abstain: {{ resolution.vote_summary.abstain }}</span>
                        <span class="text-medium-emphasis">Total: {{ resolution.vote_summary.total }}</span>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-card-text>
          </v-card>

          <!-- Meeting Minutes -->
          <v-card v-if="meeting.minutes || canEdit" class="mb-6">
            <v-card-title class="d-flex justify-space-between align-center">
              <span class="text-h6 font-weight-medium">
                <v-icon class="mr-2">mdi-text-box</v-icon>
                Meeting Minutes
              </span>
              <v-btn
                v-if="canEdit && !editingMinutes"
                size="small"
                variant="outlined"
                @click="editingMinutes = true"
              >
                {{ meeting.minutes ? 'Edit' : 'Add' }} Minutes
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div v-if="!editingMinutes">
                <div v-if="meeting.minutes">
                  <pre class="text-body-1" style="white-space: pre-wrap; font-family: inherit;">{{ meeting.minutes }}</pre>
                </div>
                <div v-else class="text-center py-6 text-medium-emphasis">
                  <v-icon size="48" class="mb-3">mdi-text-box-outline</v-icon>
                  <p>No minutes recorded yet</p>
                </div>
              </div>
              <div v-else>
                <v-textarea
                  v-model="minutesText"
                  label="Meeting Minutes"
                  variant="outlined"
                  rows="10"
                  hint="Record the key points, decisions, and action items from the meeting"
                  persistent-hint
                />
                <div class="d-flex gap-2 mt-3">
                  <v-btn
                    color="primary"
                    @click="saveMinutes"
                    :loading="savingMinutes"
                  >
                    Save Minutes
                  </v-btn>
                  <v-btn
                    variant="outlined"
                    @click="cancelMinutesEdit"
                  >
                    Cancel
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Sidebar -->
        <v-col cols="12" md="4">
          <!-- Quick Actions -->
          <v-card v-if="isAuthenticated" class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Quick Actions
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-column gap-2">
                <v-btn
                  v-if="!userAttendance && meeting.status === 'scheduled'"
                  color="success"
                  variant="flat"
                  @click="confirmAttendance"
                  prepend-icon="mdi-check"
                  block
                >
                  Confirm Attendance
                </v-btn>
                <v-btn
                  v-else-if="userAttendance?.status === 'invited'"
                  color="warning"
                  variant="flat"
                  @click="confirmAttendance"
                  prepend-icon="mdi-check"
                  block
                >
                  Confirm Attendance
                </v-btn>
                <v-btn
                  v-else-if="userAttendance?.status === 'attending' && !userAttendance?.checked_in_at && canCheckIn"
                  color="primary"
                  variant="flat"
                  @click="checkInNow"
                  prepend-icon="mdi-account-check"
                  block
                >
                  Check In Now
                </v-btn>
                <v-btn
                  v-if="meeting.virtual_link"
                  color="primary"
                  variant="outlined"
                  @click="joinMeeting"
                  prepend-icon="mdi-video"
                  block
                >
                  Join Virtual Meeting
                </v-btn>
                <v-btn
                  color="secondary"
                  variant="outlined"
                  @click="addToCalendar"
                  prepend-icon="mdi-calendar-plus"
                  block
                >
                  Add to Calendar
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- Meeting Info -->
          <v-card class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Meeting Information
            </v-card-title>
            <v-card-text>
              <div class="info-items">
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Status:</span>
                  <v-chip
                    :color="getStatusColor(meeting.status)"
                    size="small"
                    variant="flat"
                  >
                    {{ meeting.status.toUpperCase() }}
                  </v-chip>
                </div>
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Type:</span>
                  <span class="font-weight-medium">{{ meeting.meeting_type?.name }}</span>
                </div>
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Date:</span>
                  <span>{{ formatDate(meeting.start_datetime) }}</span>
                </div>
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Time:</span>
                  <span>{{ formatTimeRange(meeting.start_datetime, meeting.end_datetime) }}</span>
                </div>
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Duration:</span>
                  <span>{{ getDuration(meeting.start_datetime, meeting.end_datetime) }}</span>
                </div>
                <div v-if="meeting.location" class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Location:</span>
                  <span>{{ meeting.location }}</span>
                </div>
                <div class="info-item d-flex justify-space-between">
                  <span class="text-medium-emphasis">Organizer:</span>
                  <span>{{ meeting.creator?.full_name }}</span>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Your Attendance -->
          <v-card v-if="userAttendance" class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Your Attendance
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center mb-3">
                <v-avatar
                  :color="getAttendanceStatusColor(userAttendance.status)"
                  size="40"
                  class="mr-3"
                >
                  <v-icon color="white">
                    {{ getAttendanceIcon(userAttendance.status) }}
                  </v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-medium">{{ userAttendance.status.replace('_', ' ').toUpperCase() }}</div>
                  <div v-if="userAttendance.checked_in_at" class="text-caption">
                    Checked in: {{ formatDateTime(userAttendance.checked_in_at) }}
                  </div>
                </div>
              </div>
              <div v-if="userAttendance.notes" class="mt-3">
                <div class="text-caption text-medium-emphasis">Notes:</div>
                <div class="text-body-2">{{ userAttendance.notes }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Delete Meeting</v-card-title>
        <v-card-text>
          Are you sure you want to delete this meeting? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/store/auth'
import { useBreadcrumb } from '@/composables/useBreadcrumb'
import api from '@/services/api'

export default {
  name: 'MeetingDetails',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const toast = useToast()
    const authStore = useAuthStore()
    const { setBreadcrumbTitle } = useBreadcrumb()

    // State
    const loading = ref(true)
    const meeting = ref(null)
    const documents = ref([])
    const resolutions = ref([])
    const editingMinutes = ref(false)
    const minutesText = ref('')
    const savingMinutes = ref(false)
    const showDeleteDialog = ref(false)
    const deleting = ref(false)

    // Computed
    const isAuthenticated = computed(() => authStore.isAuthenticated)
    const currentUserId = computed(() => authStore.user?.id)
    const canEdit = computed(() => authStore.user?.is_admin || false)
    const canDelete = computed(() => authStore.user?.is_admin || false)
    const canManageAttendance = computed(() => authStore.user?.is_admin || false)

    const userAttendance = computed(() => {
      if (!meeting.value?.attendance || !currentUserId.value) return null
      return meeting.value.attendance.find(att => att.user_id === currentUserId.value)
    })

    const canCheckIn = computed(() => {
      if (!meeting.value?.start_datetime) return false
      
      const now = new Date()
      const meetingStart = new Date(meeting.value.start_datetime)
      const meetingDate = new Date(meetingStart.toDateString())
      const today = new Date(now.toDateString())
      
      // Can check in on the day of meeting after start time
      return today >= meetingDate && (today.getTime() > meetingDate.getTime() || now >= meetingStart)
    })

    // Methods
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const formatDateTime = (dateString) => {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatTime = (dateString) => {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatTimeRange = (start, end) => {
      const startTime = formatTime(start)
      const endTime = formatTime(end)
      return `${startTime} - ${endTime}`
    }

    const getDuration = (start, end) => {
      const startDate = new Date(start)
      const endDate = new Date(end)
      const diffMs = endDate - startDate
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
      
      if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes}m`
      }
      return `${diffMinutes}m`
    }

    const getStatusColor = (status) => {
      const colors = {
        'scheduled': 'primary',
        'ongoing': 'success',
        'completed': 'grey',
        'cancelled': 'error'
      }
      return colors[status] || 'grey'
    }

    const getAttendanceStatusColor = (status) => {
      const colors = {
        'invited': 'grey',
        'attending': 'success',
        'absent': 'error',
        'late': 'warning'
      }
      return colors[status] || 'grey'
    }

    const getAttendanceIcon = (status) => {
      const icons = {
        'invited': 'mdi-email',
        'attending': 'mdi-check',
        'absent': 'mdi-close',
        'late': 'mdi-clock-alert'
      }
      return icons[status] || 'mdi-account'
    }

    const getDocumentIcon = (type) => {
      const icons = {
        'agenda': 'mdi-format-list-numbered',
        'minutes': 'mdi-text-box',
        'presentation': 'mdi-presentation',
        'report': 'mdi-file-chart',
        'other': 'mdi-file'
      }
      return icons[type] || 'mdi-file'
    }

    const getDocumentColor = (type) => {
      const colors = {
        'agenda': 'primary',
        'minutes': 'success',
        'presentation': 'purple',
        'report': 'info',
        'other': 'grey'
      }
      return colors[type] || 'grey'
    }

    const getResolutionStatusColor = (status) => {
      const colors = {
        'draft': 'grey',
        'voting': 'warning',
        'approved': 'success',
        'rejected': 'error'
      }
      return colors[status] || 'grey'
    }

    const userHasVoted = (resolution) => {
      if (!resolution.votes || !currentUserId.value) return false
      return resolution.votes.some(vote => vote.user_id === currentUserId.value)
    }

    const loadMeeting = async () => {
      loading.value = true
      try {
        const response = await api.get(`/meetings/${route.params.id}`)
        meeting.value = response.data.meeting
        setBreadcrumbTitle(meeting.value.title)
        minutesText.value = meeting.value.minutes || ''

        // Load related data
        await Promise.all([
          loadMeetingDocuments(),
          loadMeetingResolutions()
        ])
      } catch (error) {
        console.error('Error loading meeting:', error)
        toast.error('Failed to load meeting')
        router.push('/meetings')
      } finally {
        loading.value = false
      }
    }

    const loadMeetingDocuments = async () => {
      try {
        const response = await api.get(`/documents?meeting_id=${route.params.id}`)
        documents.value = response.data.documents || []
      } catch (error) {
        console.error('Error loading meeting documents:', error)
      }
    }

    const loadMeetingResolutions = async () => {
      try {
        const response = await api.get(`/resolutions?meeting_id=${route.params.id}`)
        resolutions.value = response.data.resolutions || []
      } catch (error) {
        console.error('Error loading meeting resolutions:', error)
      }
    }

    const markAttendance = async (status) => {
      try {
        await api.post(`/meetings/${meeting.value.id}/attendance`, {
          status: status
        })
        
        if (status === 'attending') {
          toast.success('Attendance confirmed successfully')
        } else {
          toast.success('Attendance updated successfully')
        }
        
        await loadMeeting()
      } catch (error) {
        console.error('Error updating attendance:', error)
        
        // Check if it's a validation error from our business rules
        const errorMessage = error.response?.data?.message || 'Failed to update attendance'
        
        if (errorMessage.includes('Attendance can only be marked')) {
          // For confirmation attempts that fail due to timing, still allow "invited" status
          if (status === 'attending') {
            try {
              await api.post(`/meetings/${meeting.value.id}/attendance`, {
                status: 'invited'
              })
              toast.success('Attendance invitation confirmed - you will be marked present when the meeting starts')
              await loadMeeting()
              return
            } catch (fallbackError) {
              console.error('Fallback error:', fallbackError)
            }
          }
        }
        
        toast.error(errorMessage)
      }
    }

    const confirmAttendance = async () => {
      try {
        console.log('Confirming attendance for meeting:', meeting.value.id)
        
        const response = await api.post(`/meetings/${meeting.value.id}/attendance`, {
          status: 'attending'
        })
        
        console.log('Attendance confirmation response:', response.data)
        
        // Check if user got checked in immediately or just confirmed
        const attendance = response.data.attendance
        if (attendance?.checked_in_at) {
          toast.success('Attendance confirmed and checked in!')
        } else {
          toast.success('Attendance confirmed! You can check in when the meeting starts.')
        }
        
        await loadMeeting()
      } catch (error) {
        console.error('Error confirming attendance:', error)
        console.error('Error response:', error.response?.data)
        
        const errorMessage = error.response?.data?.message || 'Failed to confirm attendance'
        toast.error(errorMessage)
      }
    }

    const checkInNow = async () => {
      try {
        console.log('Checking in for attendance ID:', userAttendance.value.id)
        
        const response = await api.put(`/meetings/${meeting.value.id}/attendance/${userAttendance.value.id}`, {
          status: 'attending',
          checked_in_at: new Date().toISOString()
        })
        
        console.log('Check-in response:', response.data)
        toast.success('Checked in successfully!')
        await loadMeeting()
      } catch (error) {
        console.error('Error checking in:', error)
        console.error('Error response:', error.response?.data)
        
        const errorMessage = error.response?.data?.message || 'Failed to check in'
        toast.error(errorMessage)
      }
    }

    const saveMinutes = async () => {
      savingMinutes.value = true
      try {
        await api.put(`/meetings/${meeting.value.id}`, {
          minutes: minutesText.value
        })
        meeting.value.minutes = minutesText.value
        editingMinutes.value = false
        toast.success('Minutes saved successfully')
      } catch (error) {
        console.error('Error saving minutes:', error)
        toast.error('Failed to save minutes')
      } finally {
        savingMinutes.value = false
      }
    }

    const cancelMinutesEdit = () => {
      minutesText.value = meeting.value.minutes || ''
      editingMinutes.value = false
    }

    const goToVoting = (resolution) => {
      router.push(`/resolutions/${resolution.id}`)
    }

    const viewResolution = (resolution) => {
      router.push(`/resolutions/${resolution.id}`)
    }

    const viewDocument = (document) => {
      router.push(`/documents/${document.id}`)
    }

    const downloadDocument = async (document) => {
      try {
        const response = await api.get(`/documents/${document.id}/download`, {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', document.title)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Error downloading document:', error)
        toast.error('Failed to download document')
      }
    }

    const editMeeting = () => {
      router.push(`/meetings/${meeting.value.id}/edit`)
    }

    const joinMeeting = () => {
      if (meeting.value.virtual_link) {
        window.open(meeting.value.virtual_link, '_blank')
      }
    }

    const shareMeeting = () => {
      navigator.clipboard.writeText(window.location.href)
      toast.success('Meeting link copied to clipboard')
    }

    const addToCalendar = () => {
      const startDate = new Date(meeting.value.start_datetime)
      const endDate = new Date(meeting.value.end_datetime)
      
      const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(meeting.value.title)}&dates=${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z/${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z&details=${encodeURIComponent(meeting.value.description)}&location=${encodeURIComponent(meeting.value.location || '')}`
      
      window.open(googleCalendarUrl, '_blank')
    }

    const printMeeting = () => {
      window.print()
    }

    const exportMeeting = () => {
      toast.info('PDF export functionality coming soon')
    }

    const manageAttendance = () => {
      router.push(`/meetings/${meeting.value.id}/attendance`)
    }

    const deleteMeeting = () => {
      showDeleteDialog.value = true
    }

    const confirmDelete = async () => {
      deleting.value = true
      try {
        await api.delete(`/meetings/${meeting.value.id}`)
        toast.success('Meeting deleted successfully')
        router.push('/meetings')
      } catch (error) {
        console.error('Error deleting meeting:', error)
        toast.error('Failed to delete meeting')
      } finally {
        deleting.value = false
      }
    }

    // Lifecycle
    onMounted(() => {
      loadMeeting()
    })

    return {
      // State
      loading,
      meeting,
      documents,
      resolutions,
      editingMinutes,
      minutesText,
      savingMinutes,
      showDeleteDialog,
      deleting,

      // Computed
      isAuthenticated,
      currentUserId,
      canEdit,
      canDelete,
      canManageAttendance,
      userAttendance,
      canCheckIn,

      // Methods
      formatDate,
      formatDateTime,
      formatTime,
      formatTimeRange,
      getDuration,
      getStatusColor,
      getAttendanceStatusColor,
      getAttendanceIcon,
      getDocumentIcon,
      getDocumentColor,
      getResolutionStatusColor,
      userHasVoted,
      markAttendance,
      confirmAttendance,
      checkInNow,
      saveMinutes,
      cancelMinutesEdit,
      goToVoting,
      viewResolution,
      viewDocument,
      downloadDocument,
      editMeeting,
      joinMeeting,
      shareMeeting,
      addToCalendar,
      printMeeting,
      exportMeeting,
      manageAttendance,
      deleteMeeting,
      confirmDelete
    }
  }
}
</script>

<style scoped lang="scss">
.meeting-details-page {
  .text-medium-emphasis {
    opacity: 0.7;
  }

  .text-primary {
    color: rgb(var(--v-theme-primary)) !important;
  }

  .agenda-content {
    line-height: 1.6;
  }

  .attendance-card {
    transition: transform 0.2s, box-shadow 0.2s;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
    }
  }

  .document-item,
  .resolution-card {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.2s;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.04);
      transform: translateY(-1px);
    }
  }

  .vote-summary {
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .info-items {
    .info-item {
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);

      &:last-child {
        border-bottom: none;
        padding-bottom: 0;
      }
    }
  }

  // Print styles
  @media print {
    .v-btn,
    .meeting-actions {
      display: none !important;
    }
  }
}
</style>